/**
 * InstallmentsPage — Issue #329.
 *
 * 分期到期/逾期跟进页面：列表（含逾期过滤）+ 跟进登记。
 * 数据全部来自真实后端，无前端伪状态。
 */
import { useEffect, useMemo, useState } from 'react';
import Decimal from 'decimal.js';
import { useAuth } from '@/hooks/useAuth';
import {
  FINANCE_PERMISSIONS,
  hasPermission,
} from '@/lib/financePermissions';
import {
  useInstallmentItems,
  useInstallmentActions,
  type FollowUpInstallmentItemDto,
  type InstallmentItemDto,
} from '@/hooks/useFinanceExtended';

const ITEM_STATUS_LABELS: Record<string, string> = {
  pending: '待付款',
  partially_paid: '部分支付',
  paid: '已结清',
  overdue: '已逾期',
  cancelled: '已取消',
};

const ITEM_STATUS_COLORS: Record<string, string> = {
  pending: 'bg-blue-100 text-blue-800',
  partially_paid: 'bg-yellow-100 text-yellow-800',
  paid: 'bg-green-100 text-green-800',
  overdue: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-100 text-gray-500',
};

function fmtCny(amount: string): string {
  return `¥${new Decimal(amount).toFixed(2)}`;
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        ITEM_STATUS_COLORS[status] ?? 'bg-gray-100 text-gray-600'
      }`}
    >
      {ITEM_STATUS_LABELS[status] ?? status}
    </span>
  );
}

interface FollowUpDialogProps {
  item: InstallmentItemDto | null;
  onClose: () => void;
  onDone: () => void;
}

function FollowUpDialog({ item, onClose, onDone }: FollowUpDialogProps) {
  const { followUp, loading, error } = useInstallmentActions();
  const [note, setNote] = useState('');
  const [markStatus, setMarkStatus] = useState<string>('');

  if (!item) return null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!item) return;
    const ok = await followUp(item.id, {
      expectedVersion: item.version,
      note,
      status: (markStatus || undefined) as FollowUpInstallmentItemDto['status'],
    });
    if (ok) {
      onDone();
      onClose();
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <h2 className="mb-2 text-lg font-semibold">跟进逾期/到期</h2>
        <p className="mb-3 text-xs text-gray-600">
          期次 {item.sequenceNo} · 应付 {fmtCny(item.dueAmount)} ·
          已付 {fmtCny(item.paidAmount)} · 到期日{' '}
          {new Date(item.dueDate).toLocaleDateString()}
        </p>
        {error && (
          <div className="mb-3 rounded bg-red-50 px-3 py-2 text-sm text-red-700">
            {error.message}
          </div>
        )}
        <form
          onSubmit={(e) => {
            void handleSubmit(e);
          }}
          className="space-y-3"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700">跟进记录</label>
            <textarea
              required
              className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
              rows={4}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              （可选）目标状态
            </label>
            <select
              className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
              value={markStatus}
              onChange={(e) => setMarkStatus(e.target.value)}
            >
              <option value="">保持当前状态</option>
              <option value="overdue">标记为已逾期</option>
              <option value="partially_paid">部分支付</option>
              <option value="paid">已结清</option>
              <option value="cancelled">取消</option>
            </select>
          </div>
          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              className="rounded border border-gray-300 px-4 py-1.5 text-sm"
              onClick={onClose}
            >
              取消
            </button>
            <button
              type="submit"
              disabled={loading}
              className="rounded bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
            >
              {loading ? '提交中…' : '保存跟进'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function InstallmentsPage() {
  const auth = useAuth();
  const permissions = useMemo(() => auth.permissions ?? [], [auth.permissions]);
  const canRead = hasPermission(permissions, FINANCE_PERMISSIONS.INSTALLMENT_READ);
  const canFollowUp = hasPermission(
    permissions,
    FINANCE_PERMISSIONS.INSTALLMENT_FOLLOW_UP,
  );

  const [filter, setFilter] = useState<'overdue' | 'upcoming' | 'all'>('overdue');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [followUpItem, setFollowUpItem] = useState<InstallmentItemDto | null>(null);

  const { data, loading, error, fetchList } = useInstallmentItems();

  const refresh = () => {
    if (!canRead) return Promise.resolve();
    return fetchList({
      overdueOnly: filter === 'overdue',
      upcomingOnly: filter === 'upcoming',
      status: statusFilter || undefined,
      limit: 50,
    });
  };

  useEffect(() => {
    void refresh();
  }, [filter, statusFilter, canRead]);

  if (!canRead) {
    return (
      <div className="p-6 text-sm text-gray-600">您没有查看分期信息的权限。</div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">分期与逾期跟进</h1>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded border border-gray-200 bg-gray-50 p-3">
        <div>
          <label className="block text-xs font-medium text-gray-600">视图</label>
          <select
            className="mt-1 rounded border border-gray-300 px-2 py-1 text-sm"
            value={filter}
            onChange={(e) =>
              setFilter(e.target.value as 'overdue' | 'upcoming' | 'all')
            }
          >
            <option value="overdue">仅逾期</option>
            <option value="upcoming">仅 30 天内到期</option>
            <option value="all">全部</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600">状态</label>
          <select
            className="mt-1 rounded border border-gray-300 px-2 py-1 text-sm"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">全部</option>
            {Object.entries(ITEM_STATUS_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </div>
        <button
          onClick={() => void refresh()}
          className="rounded border border-gray-300 bg-white px-3 py-1 text-sm"
        >
          {loading ? '加载中…' : '刷新'}
        </button>
      </div>

      {error && (
        <div className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">
          {error.message}
        </div>
      )}

      <div className="overflow-x-auto rounded border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="px-3 py-2 text-left">期次</th>
              <th className="px-3 py-2 text-left">计划 ID</th>
              <th className="px-3 py-2 text-left">到期日</th>
              <th className="px-3 py-2 text-right">应付</th>
              <th className="px-3 py-2 text-right">已付</th>
              <th className="px-3 py-2 text-left">状态</th>
              <th className="px-3 py-2 text-left">跟进次数</th>
              <th className="px-3 py-2 text-left">最后跟进</th>
              <th className="px-3 py-2 text-left">操作</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {(data?.rows ?? []).map((row) => {
              const remaining = new Decimal(row.dueAmount).minus(row.paidAmount);
              return (
                <tr key={row.id}>
                  <td className="px-3 py-2">第 {row.sequenceNo} 期</td>
                  <td className="px-3 py-2 font-mono text-xs">{row.planId}</td>
                  <td className="px-3 py-2 text-xs">
                    {new Date(row.dueDate).toLocaleDateString()}
                  </td>
                  <td className="px-3 py-2 text-right">{fmtCny(row.dueAmount)}</td>
                  <td className="px-3 py-2 text-right">
                    {fmtCny(row.paidAmount)}{' '}
                    {!remaining.isZero() && (
                      <span className="ml-1 text-xs text-gray-500">
                        余 {fmtCny(remaining.toString())}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <StatusBadge status={row.status} />
                  </td>
                  <td className="px-3 py-2 text-xs">{row.followUpCount}</td>
                  <td className="px-3 py-2 text-xs text-gray-500">
                    {row.lastFollowUpAt
                      ? new Date(row.lastFollowUpAt).toLocaleString()
                      : '—'}
                  </td>
                  <td className="px-3 py-2">
                    {canFollowUp && row.status !== 'paid' && row.status !== 'cancelled' && (
                      <button
                        onClick={() => setFollowUpItem(row)}
                        className="rounded border border-blue-300 bg-blue-50 px-2 py-0.5 text-xs text-blue-700"
                      >
                        跟进
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {(data?.rows ?? []).length === 0 && !loading && (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center text-gray-500">
                  暂无满足条件的分期项
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <FollowUpDialog
        key={followUpItem?.id ?? 'none'}
        item={followUpItem}
        onClose={() => setFollowUpItem(null)}
        onDone={() => void refresh()}
      />
    </div>
  );
}
